/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useRef } from 'react';
import { IEmployeeViewModel, IDepartmentViewModel, IPositionViewModel } from '@/types/employee';
import {
  getOrganizationIndexedDB,
  clearOrganizationCache,
  fetchInitDataOrganization,
} from '@/utils/organizationIndexedDBUtils';
import { PeriodDTO } from '@/types/period';
import { PerspectiveDTO } from '@/types/step';
import { computeSubOrganization } from '@/utils/orgScope';

export interface CacheOptions {
  forceRefresh?: boolean;
  cacheExpiry?: number;
  maxCacheAge?: number;
}

export interface OrganizationCacheResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  fromCache: boolean;
}

/** Chỉ dùng để dedupe gọi API khi cache rỗng, không lưu kết quả vào RAM */
let loadingPromise: Promise<any> | null = null;

/**
 * Clear cache (IndexedDB) - exported for external use
 */
export async function clearGlobalCache() {
  loadingPromise = null;
  await clearOrganizationCache();
}

/**
 * Hook for caching organization data (employees, departments, positions)
 * Checks IndexedDB first, then falls back to API calls
 */
export function useOrganizationCache() {
  const loadingRef = useRef<boolean>(false);
  /**
   * Get employees - chỉ lấy từ cache, nếu rỗng thì gọi company API
   */
  const getPeriods = useCallback(async (): Promise<PeriodDTO[]> => {
    const organizationData = await getOrganizationIndexedDB();
    const periods = organizationData?.period || [];

    if (Array.isArray(periods) && periods.length > 0) {
      return periods as unknown as PeriodDTO[];
    }

    const companyResult = await getCompanys();
    return (companyResult.period || []) as unknown as PeriodDTO[];
  }, []);

  /**
   * Get employees - chỉ lấy từ cache, nếu rỗng thì gọi company API
   */
  const getEmployees = useCallback(async (): Promise<IEmployeeViewModel[]> => {
    const organizationData = await getOrganizationIndexedDB();
    const employees = organizationData?.employees || [];

    if (Array.isArray(employees) && employees.length > 0) {
      return employees as unknown as IEmployeeViewModel[];
    }

    const companyResult = await getCompanys();
    return (companyResult.employees || []) as unknown as IEmployeeViewModel[];
  }, []);

  /**
   * Get Employee By ID - chỉ lấy từ cache, nếu rỗng thì gọi company API
   */
  const getEmployeeByID = useCallback(async (id: string): Promise<IEmployeeViewModel | null> => {
    const employees = await getEmployees();
    if (Array.isArray(employees) && employees.length > 0) {
      // Tìm employee theo ID
      const employee = employees.find((emp) => emp.id === id || emp.ID === id);
      return employee || null;
    }

    return null;
  }, []);

  /**
   * Get departments - chỉ lấy từ cache, nếu rỗng thì gọi company API
   */
  const getDepartments = useCallback(async (): Promise<IDepartmentViewModel[]> => {
    const organizationData = await getOrganizationIndexedDB();
    const departments = organizationData?.departments || [];

    if (Array.isArray(departments) && departments.length > 0) {
      return departments as unknown as IDepartmentViewModel[];
    }

    const companyResult = await getCompanys();
    return (companyResult.departments || []) as unknown as IDepartmentViewModel[];
  }, []);

  /**
   * Get department By ID - chỉ lấy từ cache, nếu rỗng thì gọi company API
   */
  const getDepartmentByID = useCallback(async (id: string): Promise<IDepartmentViewModel | null> => {
    const departments = await getDepartments();
    if (Array.isArray(departments) && departments.length > 0) {
      // Tìm employee theo ID
      const department = departments.find((dep) => dep.id === id);
      return department || null;
    }
    return null;
  }, []);

  const getSubOrganization = useCallback(
    async (
      profile: any,
      roleVuotCap: boolean,
      roleNgangCap: boolean,
      options?: { treatAsQuanLy?: boolean; useFullOrg?: boolean }
    ): Promise<{ departments: IDepartmentViewModel[]; employees: IEmployeeViewModel[] }> => {
      const departments = await getDepartments();
      const employees = await getEmployees();
      return computeSubOrganization(profile, departments, employees, roleVuotCap, roleNgangCap, options);
    },
    [getDepartments, getEmployees]
  );

  /**
   * Kiểm tra xem profile có quyền truy cập vào tổ chức con của idSelected không
   * @param profile Thông tin người dùng hiện tại
   * @param idSelected ID phòng ban hoặc nhân viên cần kiểm tra
   * @returns true nếu có quyền, false nếu không có quyền
   */
  const hasSubOrganization = useCallback(
    async (profile: any, idSelected: string | null | undefined): Promise<boolean> => {
      // Admin => return true
      if (profile?.isAdmin) return true;

      // Member => return profile?.id === idSelected
      if (!profile?.isQuanLy) {
        return profile?.id === idSelected;
      }

      // Quản lý: lấy phòng ban hiện tại và tất cả phòng ban con, bao gồm cả nhân viên
      if (profile?.isQuanLy) {
        const bpId = profile?.boPhanId;
        if (!bpId) return profile?.id === idSelected;

        const departments = await getDepartments();
        const employees = await getEmployees();

        // Hàm đệ quy để lấy tất cả phòng ban con
        const getAllSubDepartmentIds = (parentId: string): Set<string> => {
          const subDeptIds = new Set<string>([parentId]);

          const findChildren = (deptId: string) => {
            departments.forEach((dept) => {
              const deptParentId = (dept as any).parentID || (dept as any).ParentID || '';
              if (deptParentId === deptId) {
                const deptIdValue = dept.id || (dept as any).ID || '';
                if (deptIdValue && !subDeptIds.has(deptIdValue)) {
                  subDeptIds.add(deptIdValue);
                  findChildren(deptIdValue); // Đệ quy
                }
              }
            });
          };

          findChildren(parentId);
          return subDeptIds;
        };

        // Lấy tất cả ID phòng ban (bao gồm phòng ban hiện tại và các phòng ban con)
        const allowedDepartmentIds = getAllSubDepartmentIds(bpId);

        // Kiểm tra nếu idSelected là phòng ban
        if (idSelected && allowedDepartmentIds.has(idSelected)) {
          return true;
        }

        // Kiểm tra nếu idSelected là nhân viên thuộc các phòng ban này
        const employee = employees.find((emp) => {
          const empId = emp.id || (emp as any).ID || '';
          const empBpId = emp.bpid || (emp as any).BPID || '';
          return empId === idSelected && allowedDepartmentIds.has(empBpId);
        });

        return !!employee;
      }

      return profile?.id === idSelected;
    },
    [getDepartments, getEmployees]
  );

  /**
   * Get positions - chỉ lấy từ cache, nếu rỗng thì gọi company API
   */
  const getPositions = useCallback(async (): Promise<IPositionViewModel[]> => {
    const organizationData = await getOrganizationIndexedDB();
    const positions = organizationData?.positions || [];

    if (Array.isArray(positions) && positions.length > 0) {
      return positions as unknown as IPositionViewModel[];
    }

    const companyResult = await getCompanys();
    return (companyResult.positions || []) as unknown as IPositionViewModel[];
  }, []);

  /**
   * Get units - chỉ lấy từ cache, nếu rỗng thì gọi company API
   */
  const getUnits = useCallback(async (): Promise<any[]> => {
    const organizationData = await getOrganizationIndexedDB();
    const units = organizationData?.units || [];
    console.log('[UnitsSync] getUnits from IndexedDB', {
      count: Array.isArray(units) ? units.length : 0,
      labels: (Array.isArray(units) ? units : []).map((u: { Ten?: string; ten?: string }) => u.Ten ?? u.ten ?? ''),
    });
    if (Array.isArray(units) && units.length > 0) {
      return units;
    }

    // Cache rỗng, gọi company API
    const companyResult = await getCompanys();
    console.log('[UnitsSync] getUnits fallback getCompanys', {
      count: companyResult.units?.length ?? 0,
    });
    return companyResult.units || [];
  }, []);

  /**
   * Get perspectives - chỉ lấy từ cache, nếu rỗng thì gọi company API
   */
  const getPerspectives = useCallback(async (): Promise<PerspectiveDTO[]> => {
    const organizationData = await getOrganizationIndexedDB();
    const perspectives = organizationData?.perspectives || [];

    if (Array.isArray(perspectives) && perspectives.length > 0) {
      return perspectives as unknown as PerspectiveDTO[];
    }

    const companyResult = await getCompanys();
    return (companyResult.perspectives || []) as unknown as PerspectiveDTO[];
  }, []);

  const emptyCompanyResult = () => ({
    employees: [],
    departments: [],
    positions: [],
    units: [],
    roles: [],
    period: [],
    perspectives: [],
    version: '',
  });

  const toCompanyResult = (org: any) => ({
    employees: org?.employees ?? [],
    departments: org?.departments ?? [],
    positions: org?.positions ?? [],
    units: org?.units ?? [],
    roles: org?.roles ?? [],
    period: org?.period ?? [],
    perspectives: org?.perspectives ?? [],
    version: org?.version ?? '',
  });

  const isCacheFull = (org: any) =>
    org &&
    (org.employees?.length ?? 0) > 0 &&
    (org.departments?.length ?? 0) > 0 &&
    (org.positions?.length ?? 0) > 0 &&
    (org.units?.length ?? 0) > 0 &&
    (org.roles?.length ?? 0) > 0 &&
    (org.perspectives?.length ?? 0) > 0;

  const getCompanys = useCallback(async (): Promise<any> => {
    try {
      const data = await getOrganizationIndexedDB();
      if (isCacheFull(data)) {
        return toCompanyResult(data);
      }

      if (loadingPromise) {
        await loadingPromise;
        const after = await getOrganizationIndexedDB();
        return toCompanyResult(after);
      }

      loadingRef.current = true;
      loadingPromise = (async () => {
        try {
          const result = await fetchInitDataOrganization();
          if (!result.success) return emptyCompanyResult();
          const saved = await getOrganizationIndexedDB();
          return toCompanyResult(saved);
        } catch (err) {
          console.error('getCompanys error:', err);
          return emptyCompanyResult();
        } finally {
          loadingRef.current = false;
          loadingPromise = null;
        }
      })();

      return await loadingPromise;
    } catch (error) {
      console.error('getCompanys error:', error);
      loadingRef.current = false;
      loadingPromise = null;
      return emptyCompanyResult();
    }
  }, []);

  /**
   * Clear all organization cache
   */
  const clearAllCache = useCallback(async (): Promise<boolean> => {
    try {
      loadingPromise = null;
      loadingRef.current = false;
      return await clearOrganizationCache();
    } catch (err) {
      console.error('Failed to clear cache:', err);
      return false;
    }
  }, []);

  return {
    // Main functions
    getPeriods,
    getEmployees,
    getEmployeeByID,
    getDepartmentByID,
    getDepartments,
    getPositions,
    getUnits,
    getPerspectives,
    getCompanys,
    clearAllCache,
    hasSubOrganization,
    getSubOrganization,
  };
}

export default useOrganizationCache;
